  import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
  import { CommonModule, isPlatformBrowser } from '@angular/common';
  import { MatCardModule } from '@angular/material/card';
  import { MatButtonModule } from '@angular/material/button';
  import { MatIconModule } from '@angular/material/icon';
  import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
  import { RouterModule } from '@angular/router';
  import { ProductStoreService, Product } from '../../services/productstore.service';

  @Component({
    selector: 'app-home',
    standalone: true,
    imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, RouterModule],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    animations: [
      trigger('fadeInUp', [
        transition(':enter', [
          style({ opacity: 0, transform: 'translateY(40px)' }),
          animate('800ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ opacity: 1, transform: 'translateY(0)' })
          )
        ])
      ]),
      trigger('slideIn', [
        transition(':enter', [
          style({ opacity: 0, transform: 'translateX(-30px)' }),
          animate('600ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ opacity: 1, transform: 'translateX(0)' })
          )
        ])
      ]),
      trigger('staggerCards', [
        transition(':enter', [
          query('.product-card', [
            style({ opacity: 0, transform: 'translateY(40px) scale(0.95)' }),
            stagger(150, [
              animate('600ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                style({ opacity: 1, transform: 'translateY(0) scale(1)' })
              )
            ])
          ], { optional: true })
        ])
      ])
    ]
  })
  export class HomeComponent implements OnInit {
    // Product Details Modal
    showProductDetails = false;
    selectedProduct: Product | null = null;
    
    // Dynamic products from API
    products: Product[] = [];
    isLoading = true;

    heroMetrics = [
      { icon: '🏭', number: '15+', label: 'Years Experience' },
      { icon: '🌍', number: '50+', label: 'Countries Export' },
      { icon: '🤝', number: '1000+', label: 'Business Partners' }
    ];

    features = [
      {
        icon: '🎯',
        title: 'Premium Manufacturing',
        description: 'State-of-the-art facilities with ISO certification ensuring world-class quality standards for global markets.'
      },
      {
        icon: '🚀',
        title: 'Global Export Excellence',
        description: 'Seamless export services to 50+ countries with comprehensive logistics and documentation support.'
      },
      {
        icon: '💎',
        title: 'Custom Solutions',
        description: 'Tailored bag manufacturing solutions for B2B clients with private labeling and custom design services.'
      }
    ];

    constructor(
      @Inject(PLATFORM_ID) private platformId: Object,
      private productStore: ProductStoreService
    ) { }

    ngOnInit(): void {
      this.loadHomeProducts();
    }

    loadHomeProducts(): void {
      this.isLoading = true;
      
      // Load products directly from API
      this.productStore.getAllProducts().subscribe({
        next: (response) => {
          if ((response.success || response.status === 'success') && response.data) {
            // Transform and filter products for home page
            const allProducts = response.data.map((rawProduct: any) => this.transformProduct(rawProduct));
            this.products = allProducts.filter((product: Product) => this.shouldShowOnHome(product));
          } else {
            this.products = [];
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.products = [];
          this.isLoading = false;
        }
      });
    }

    // Transform raw API product to our Product interface
    private transformProduct(rawProduct: any): Product {
      return {
        _id: rawProduct.id?.toString(),
        id: rawProduct.id,
        name: rawProduct.name,
        category: rawProduct.category,
        categoryId: rawProduct.category_id || rawProduct.category,
        price: Number(rawProduct.price),
        originalPrice: Number(rawProduct.original_price || rawProduct.originalPrice || 0),
        image: rawProduct.image,
        images: Array.isArray(rawProduct.images) ? rawProduct.images : [],
        rating: Number(rawProduct.rating || 0),
        reviews: Number(rawProduct.reviews || 0),
        isNew: this.parseBoolean(rawProduct.is_new || rawProduct.isNew),
        isSale: this.parseBoolean(rawProduct.is_sale || rawProduct.isSale),
        description: rawProduct.description || '',
        shortDescription: rawProduct.short_description || rawProduct.shortDescription,
        isCustomizable: this.parseBoolean(rawProduct.is_customizable || rawProduct.isCustomizable),
        customizationOptions: Array.isArray(rawProduct.customization_options) 
          ? rawProduct.customization_options 
          : (Array.isArray(rawProduct.customizationOptions) ? rawProduct.customizationOptions : []),
        showOnHome: this.parseBoolean(rawProduct.show_on_home || rawProduct.showOnHome),
        showOnProducts: this.parseBoolean(rawProduct.show_on_products || rawProduct.showOnProducts),
        status: rawProduct.status || 'active'
      };
    }

    // Helper method to parse boolean values from API response
    private parseBoolean(value: any): boolean {
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
      }
      return false;
    }

    // Check if product should show on home page
    private shouldShowOnHome(product: Product): boolean {
      let shouldShow = true;
      if (product.showOnHome !== undefined && product.showOnHome !== null) {
        shouldShow = product.showOnHome;
      }
      return shouldShow;
    }

    scrollToProducts(): void {
      if (isPlatformBrowser(this.platformId)) {
        const element = document.getElementById('products');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }

    exploreCollection(): void {
      // Navigate to products page
      if (isPlatformBrowser(this.platformId)) {
        window.location.href = '/products';
      }
    }

    // Product Details Modal Functions
    viewProductDetails(product: Product): void {
      this.selectedProduct = product;
      this.showProductDetails = true;
      // Prevent body scroll when modal is open
      if (isPlatformBrowser(this.platformId)) {
        document.body.style.overflow = 'hidden';
      }
    }

    closeProductDetails(): void {
      this.showProductDetails = false;
      this.selectedProduct = null;
      // Restore body scroll
      if (isPlatformBrowser(this.platformId)) {
        document.body.style.overflow = 'auto';
      }
    }

    getStars(rating: number): number[] {
      return Array(5).fill(0).map((_, i) => i < Math.floor(rating) ? 1 : 0);
    }

    getCategoryDisplayName(category: string): string {
      const categoryMap: { [key: string]: string } = {
        'field-sample-bag': 'Field Sample Bags',
        'cotton-bags': 'Cotton Bags',
        'canvas-tote-bags': 'Canvas Tote Bags',
        'jute-bags': 'Jute Bags'
      };
      return categoryMap[category] || category;
    }

    // WhatsApp Functions
    knowMoreOnWhatsApp(product: Product): void {
      let message = `Hello Aadinath Enterprises!

  I'm interested in your product: *${product.name}*

  Product Details:
  - Price: ₹${product.price}
  - Category: ${this.getCategoryDisplayName(product.category)}
  - Rating: ${product.rating}/5`;

      if (product.customizationOptions && product.customizationOptions.length > 0) {
        message += `\n- Customization Options: ${product.customizationOptions.join(', ')}`;
      }

      message += `\n\nCould you please provide more information about:
  - Minimum order quantity
  - Bulk pricing
  - Customization details
  - Delivery timeline

  Thank you!`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/919768301159?text=${encodedMessage}`;
      
      if (isPlatformBrowser(this.platformId)) {
        window.open(whatsappUrl, '_blank');
      }
    }

    onImageError(event: any) {
      event.target.src = '/assets/images/placeholder.jpg';
    }
  }
